<template>
  <div class="task-search-container">
    <!-- Search input -->
    <div class="search-input-wrapper">
      <i class="fa fa-search search-icon"></i>
      <input 
        type="text"
        class="search-input"
        placeholder="Search tasks and comments..."
        v-model="searchQuery"
        @input="handleSearchInput"
        ref="searchInput"
      />
      <i v-if="isSearching" class="fa fa-spinner fa-spin search-spinner"></i>
    </div>
    
    <!-- Results dropdown - only show when query meets minimum length -->
    <div v-if="searchQuery && searchQuery.length >= minQueryLength" class="search-results">
      <!-- Searching indicator -->
      <div v-if="isSearching" class="search-loading">
        <i class="fa fa-spinner fa-spin"></i> Searching...
      </div>
      
      <!-- Visual results display -->
      <template v-else>
        <div class="results-section">
          <div class="section-title">Tasks</div>
          <div v-if="taskResults.length > 0" class="results-list">
            <div v-for="(task, index) in taskResults" 
                 :key="`task-${index}`" 
                 class="result-item" 
                 @click="handleTaskClick(task.id)">
              <i class="fa fa-tasks"></i>
              <span v-html="task.highlightedContent || task.content"></span>
            </div>
          </div>
          <div v-else class="no-results">
            No matching tasks found
          </div>
        </div>
        
        <div class="results-section">
          <div class="section-title">Comments</div>
          <div v-if="commentResults.length > 0" class="results-list">
            <div v-for="(comment, index) in commentResults" 
                 :key="`comment-${index}`" 
                 class="result-item" 
                 @click="handleTaskClick(comment.taskId)">
              <i class="fa fa-comment"></i>
              <span v-html="comment.highlightedText || comment.text"></span>
            </div>
          </div>
          <div v-else class="no-results">
            No matching comments found
          </div>
        </div>
        
        <div v-if="taskResults.length === 0 && commentResults.length === 0" class="search-footer">
          <div class="search-help">
            No results found. Try a different search term.
          </div>
        </div>
      </template>
    </div>
    
    <!-- Minimum character hint -->
    <div v-else-if="searchQuery && searchQuery.length > 0 && searchQuery.length < minQueryLength" class="search-hint">
      Type at least {{ minQueryLength }} characters to search
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'TaskSearch',
  props: {
    searchClient: {
      type: Object,
      required: true
    },
    siteID: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      searchQuery: '',
      taskResults: [],
      commentResults: [],
      isSearching: false,
      minQueryLength: 2, // Minimum characters before searching (reduces quota usage)
      debounceDelay: 400, // Milliseconds to wait after typing stops (reduces quota usage)
      searchAbortController: null
    };
  },
  watch: {
    searchQuery: {
      handler(newValue) {
        // Clear results if query is empty or too short
        if (!newValue || newValue.length < this.minQueryLength) {
          this.taskResults = [];
          this.commentResults = [];
          this.isSearching = false;
          return;
        }
        
        // Show searching indicator immediately for better UX
        this.isSearching = true;
        
        // Debounced search - waits for user to stop typing
        this.debouncedSearch(newValue);
      },
      immediate: false
    }
  },
  created() {
    // Create debounced search function (400ms delay saves significant quota)
    this.debouncedSearch = _.debounce(this.performSearch, this.debounceDelay);
  },
  beforeDestroy() {
    // Cancel any pending debounced calls
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel();
    }
  },
  methods: {
    handleSearchInput() {
      this.$emit('search', this.searchQuery);
    },
    
    /**
     * Performs a single batched search across both indices using multipleQueries()
     * This is a KEY optimization: instead of 2 API calls per search, we make 1
     */
    async performSearch(query) {
      if (!query || query.length < this.minQueryLength) {
        this.isSearching = false;
        return;
      }
      
      try {
        // Use multipleQueries to search BOTH indices in ONE API call
        // This cuts Algolia quota usage in HALF compared to separate searches
        const results = await this.searchClient.search([
          {
            indexName: 'teamio-tasks',
            query: query,
            params: {
              hitsPerPage: 5,
              filters: `siteID:${this.siteID}`,
              attributesToHighlight: ['content'],
              highlightPreTag: '<mark>',
              highlightPostTag: '</mark>'
            }
          },
          {
            indexName: 'teamio-task-comments',
            query: query,
            params: {
              hitsPerPage: 5,
              filters: `siteID:${this.siteID}`,
              attributesToHighlight: ['text'],
              highlightPreTag: '<mark>',
              highlightPostTag: '</mark>'
            }
          }
        ]);
        
        // Process task results (first index)
        if (results.results && results.results[0]) {
          const taskHits = results.results[0].hits || [];
          this.taskResults = taskHits.map(item => ({
            id: item.objectID,
            content: item.content || 'Untitled task',
            highlightedContent: item._highlightResult?.content?.value || item.content || 'Untitled task'
          }));
        } else {
          this.taskResults = [];
        }
        
        // Process comment results (second index)
        if (results.results && results.results[1]) {
          const commentHits = results.results[1].hits || [];
          this.commentResults = commentHits.map(item => ({
            taskId: item.taskID,
            text: item.text || 'No comment text',
            highlightedText: item._highlightResult?.text?.value || item.text || 'No comment text'
          }));
        } else {
          this.commentResults = [];
        }
        
      } catch (error) {
        console.error("Error fetching Algolia results:", error);
        this.taskResults = [];
        this.commentResults = [];
      } finally {
        this.isSearching = false;
      }
    },
    
    handleTaskClick(taskId) {
      console.log(`Clicked task: ${taskId}`);
      this.$emit('task-click', taskId);
      this.searchQuery = ''; // Clear search after selection
      this.taskResults = [];
      this.commentResults = [];
    },
    
    focusInput() {
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus();
      }
    },
    
    clearSearch() {
      this.searchQuery = '';
      this.taskResults = [];
      this.commentResults = [];
      this.isSearching = false;
    }
  },
  mounted() {
    // Initialize with empty results
    this.taskResults = [];
    this.commentResults = [];
    
    // Note: We intentionally DO NOT focus this input on mount
    // The focus should be on the task input field instead
  }
};
</script>

<style lang="scss" scoped>
.task-search-container {
  width: 100%;
  min-width: 0; /* Allow shrinking */
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background-color: white;
  transition: all 0.2s ease-in-out;
}

.search-input-wrapper:focus-within {
  border-color: #3abf94;
  box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.3);
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 14px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 38px;
  padding: 8px 12px 8px 35px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  background-color: transparent;
}

.search-input:focus {
  outline: none;
}

.search-results {
  position: absolute;
  top: calc(100% + 5px); /* Add small gap below input */
  left: 0;
  right: 0;
  z-index: 1000; /* Below tags dropdown (1050), above header (20) */
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  margin-top: 4px;
  max-height: 300px;
  overflow-y: auto;
}

.section-title {
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  background-color: #f8f8f8;
  border-bottom: 1px solid #eee;
}

.results-list {
  padding: 4px 0;
}

.result-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.result-item:hover {
  background-color: #f5f5f5;
}

.result-item i {
  margin-right: 8px;
  color: #3abf94;
  flex-shrink: 0;
}

.result-item span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-results {
  padding: 12px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.search-footer {
  padding: 8px 12px;
  text-align: center;
  background-color: #f8f8f8;
  border-top: 1px solid #eee;
}

.search-help {
  font-size: 12px;
  color: #666;
  font-style: italic;
}

.search-spinner {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #3abf94;
  font-size: 14px;
}

.search-loading {
  padding: 16px;
  text-align: center;
  color: #666;
}

.search-loading i {
  margin-right: 8px;
  color: #3abf94;
}

.search-hint {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
}

mark {
  background-color: #fff3cd;
  padding: 0 2px;
  border-radius: 2px;
}
</style> 